// Project utility functions and constants
import { projectsData } from '../data/projectsData';

// Project categories for filtering and organization
export const PROJECT_CATEGORIES = {
  ECOMMERCE: 'E-commerce & AR',
  MANAGEMENT: 'Management System',
  NAVIGATION: 'Navigation & Transport',
  REAL_ESTATE: 'Real Estate & AR',
  ACADEMIC: 'Academic & Research'
};

// Project status types
export const PROJECT_STATUS = {
  COMPLETED: 'completed',
  IN_PROGRESS: 'in_progress',
  ARCHIVED: 'archived'
};

// Technology categories for filtering
export const TECH_CATEGORIES = {
  MOBILE: ['Flutter', 'Dart', 'Android', 'iOS'],
  BACKEND: ['Firebase', 'Firestore', 'Node.js', 'Express'],
  AR_VR: ['ARCore', 'ARKit', 'Unity', 'AR/VR'],
  AI_ML: ['AI/ML', 'Natural Language Processing', 'Machine Learning'],
  DATABASE: ['SQLite', 'Firestore', 'MongoDB', 'PostgreSQL'],
  AUTHENTICATION: ['Google OAuth', 'Firebase Auth', 'OAuth', 'JWT']
};

// Helper function to get project statistics
export const getProjectStatistics = () => {
  const projects = Object.values(projectsData);
  
  return {
    totalProjects: projects.length,
    categories: [...new Set(projects.map(p => p.category))],
    technologies: [...new Set(projects.flatMap(p => p.techStack))],
    projectsByCategory: Object.entries(PROJECT_CATEGORIES).reduce((acc, [key, category]) => {
      acc[category] = projects.filter(p => p.category === category).length;
      return acc;
    }, {})
  };
};

// Helper function to get projects sorted by different criteria
export const getSortedProjects = (sortBy = 'title') => {
  const projects = Object.entries(projectsData);
  
  switch (sortBy) {
    case 'title':
      return projects.sort(([, a], [, b]) => a.title.localeCompare(b.title));
    case 'category':
      return projects.sort(([, a], [, b]) => a.category.localeCompare(b.category));
    case 'techCount':
      return projects.sort(([, a], [, b]) => b.techStack.length - a.techStack.length);
    default:
      return projects;
  }
};

// Helper function to validate project data structure
export const validateProjectData = (projectId) => {
  const project = projectsData[projectId];
  if (!project) return { valid: false, errors: ['Project not found'] };
  
  const errors = [];
  const requiredFields = ['title', 'description', 'techStack', 'githubLink', 'contactEmail'];
  
  requiredFields.forEach(field => {
    if (!project[field]) {
      errors.push(`Missing required field: ${field}`);
    }
  });
  
  if (project.techStack && !Array.isArray(project.techStack)) {
    errors.push('techStack must be an array');
  }
  
  if (project.features && !Array.isArray(project.features)) {
    errors.push('features must be an array');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

// Helper function to get projects by technology
export const getProjectsByTechnology = (technology) => {
  return Object.entries(projectsData)
    .filter(([, project]) => 
      project.techStack.some(tech => 
        tech.toLowerCase().includes(technology.toLowerCase())
      )
    )
    .reduce((acc, [key, project]) => {
      acc[key] = project;
      return acc;
    }, {});
};

// Helper function to format project data for display
export const formatProjectForDisplay = (projectId) => {
  const project = projectsData[projectId];
  if (!project) return null;
  
  return {
    id: projectId,
    ...project,
    techStackFormatted: project.techStack.join(', '),
    hasScreenshots: !!(project.screenshotSections && project.screenshotSections.length > 0),
    hasReport: !!project.projectReport,
    hasAPK: !!project.apkDownload,
    featureCount: project.features ? project.features.length : 0,
    highlightCount: project.keyHighlights ? project.keyHighlights.length : 0
  };
};

// Helper function to get project recommendations based on current project
export const getRelatedProjects = (currentProjectId, limit = 3) => {
  const currentProject = projectsData[currentProjectId];
  if (!currentProject) return [];
  
  const allProjects = Object.entries(projectsData)
    .filter(([id]) => id !== currentProjectId);
  
  // Score projects based on similar technologies and category
  const scoredProjects = allProjects.map(([id, project]) => {
    let score = 0;
    
    // Same category gets higher score
    if (project.category === currentProject.category) {
      score += 3;
    }
    
    // Common technologies get points
    const commonTech = project.techStack.filter(tech => 
      currentProject.techStack.includes(tech)
    );
    score += commonTech.length;
    
    return { id, project, score };
  });
  
  // Sort by score and return top recommendations
  return scoredProjects
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ id, project }) => ({ id, ...project }));
};

// Helper function to generate project URL slug
export const generateProjectSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')     // Replace spaces with hyphens
    .replace(/-+/g, '-')      // Replace multiple hyphens with single
    .trim();
};

// Helper function to get project metadata for SEO
export const getProjectMetadata = (projectId) => {
  const project = projectsData[projectId];
  if (!project) return null;
  
  return {
    title: `${project.title} | Portfolio`,
    description: project.shortDescription || project.description.substring(0, 160),
    keywords: project.techStack.join(', '),
    author: 'Sakib Ahmed Shanto',
    url: `/project/${projectId}`,
    image: project.screenshotSections?.[0]?.screenshots?.[0]?.src || null
  };
};

const projectUtils = {
  PROJECT_CATEGORIES,
  PROJECT_STATUS,
  TECH_CATEGORIES,
  getProjectStatistics,
  getSortedProjects,
  validateProjectData,
  getProjectsByTechnology,
  formatProjectForDisplay,
  getRelatedProjects,
  generateProjectSlug,
  getProjectMetadata
};

export default projectUtils;
